import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    TextField,
    MenuItem,
    Grid,
    FormControlLabel,
    Switch,
    IconButton,
    Tooltip,
    Snackbar,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    EventBusy as SuspensionIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface Suspension {
    id: string;
    diary_id: string;
    suspension_type: string;
    from_date: string;
    to_date: string;
    reason: string;
    description?: string;
    notify_patients: boolean;
    active: boolean;
    created_at: string;
    recup_id?: string;
}

interface SuspensionForm {
    suspensionType: string;
    fromDate: string;
    toDate: string;
    reason: string;
    description: string;
    notifyPatients: boolean;
}

interface SuspensionsDialogProps {
    open: boolean;
    onClose: () => void;
    diaryId: string;
    diaryName: string;
}

const SUSPENSION_TYPES = [
    { value: 'TEMPORARY', label: 'Temporanea', color: 'warning' as const },
    { value: 'PERMANENT', label: 'Permanente', color: 'error' as const },
    { value: 'HOLIDAY', label: 'Festività', color: 'info' as const },
    { value: 'MAINTENANCE', label: 'Manutenzione', color: 'secondary' as const },
    { value: 'OTHER', label: 'Altro', color: 'default' as const }
];

const SuspensionsDialog: React.FC<SuspensionsDialogProps> = ({
    open,
    onClose,
    diaryId,
    diaryName
}) => {
    const [suspensions, setSuspensions] = useState<Suspension[]>([]);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingSuspension, setEditingSuspension] = useState<Suspension | null>(null);
    
    // Form state
    const [form, setForm] = useState<SuspensionForm>({
        suspensionType: 'TEMPORARY',
        fromDate: new Date().toISOString().split('T')[0],
        toDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
        reason: '',
        description: '',
        notifyPatients: true
    });
    
    // Snackbar
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    // Load suspensions when dialog opens
    useEffect(() => {
        if (open && diaryId) {
            loadSuspensions();
        }
    }, [open, diaryId]);

    const loadSuspensions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/suspensions/${diaryId}?includeExpired=false`);
            if (response.data.success) {
                setSuspensions(response.data.data || []);
            } else {
                setError(response.data.error || 'Errore nel caricamento delle sospensioni');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Errore nel caricamento delle sospensioni');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSuspension = async () => {
        // Validazione
        if (!form.reason.trim()) {
            setSnackbar({ 
                open: true, 
                message: 'Il motivo è obbligatorio', 
                severity: 'error' 
            });
            return;
        }

        if (new Date(form.fromDate) >= new Date(form.toDate)) {
            setSnackbar({ 
                open: true, 
                message: 'La data di fine deve essere successiva alla data di inizio', 
                severity: 'error' 
            });
            return;
        }

        setFormLoading(true);
        try {
            const payload = {
                diaryId,
                suspensionType: form.suspensionType,
                fromDate: form.fromDate,
                toDate: form.toDate,
                reason: form.reason.trim(),
                description: form.description.trim() || undefined,
                notifyPatients: form.notifyPatients
            };

            const response = await apiClient.post('/suspensions/create-recup', payload);
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Sospensione creata con successo su RECUP!', 
                    severity: 'success' 
                });
                
                // Reset form
                setForm({
                    suspensionType: 'TEMPORARY',
                    fromDate: new Date().toISOString().split('T')[0],
                    toDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    reason: '',
                    description: '',
                    notifyPatients: true
                });
                
                setShowForm(false);
                await loadSuspensions(); // Reload list
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nella creazione della sospensione', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nella creazione della sospensione', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteSuspension = async (suspension: Suspension) => {
        const confirmed = window.confirm(
            `Sei sicuro di voler eliminare la sospensione "${suspension.reason}"?\n\n` +
            `Periodo: ${formatDate(suspension.from_date)} - ${formatDate(suspension.to_date)}\n\n` +
            `L'eliminazione avverrà anche su RECUP.`
        );
        
        if (!confirmed) return;

        try {
            const response = await apiClient.delete(`/suspensions/${suspension.recup_id || suspension.id}/delete-recup`, {
                data: { reason: 'Eliminazione richiesta dall\'utente' }
            });
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Sospensione eliminata con successo su RECUP!', 
                    severity: 'success' 
                });
                await loadSuspensions(); // Reload list
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'eliminazione della sospensione', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'eliminazione della sospensione', 
                severity: 'error' 
            });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSuspensionTypeInfo = (type: string) => {
        return SUSPENSION_TYPES.find(t => t.value === type) || SUSPENSION_TYPES[4];
    };

    const isActiveSuspension = (suspension: Suspension) => {
        const now = new Date();
        const from = new Date(suspension.from_date);
        const to = new Date(suspension.to_date);
        return suspension.active && from <= now && now <= to;
    };

    const handleFormChange = (field: keyof SuspensionForm, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh' }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SuspensionIcon />
                            <Typography variant="h6">
                                Sospensioni Agenda: {diaryName}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Statistiche */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Totale Sospensioni
                                    </Typography>
                                    <Typography variant="h4">
                                        {suspensions.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Attive Ora
                                    </Typography>
                                    <Typography variant="h4" color="warning.main">
                                        {suspensions.filter(isActiveSuspension).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Future
                                    </Typography>
                                    <Typography variant="h4" color="info.main">
                                        {suspensions.filter(s => s.active && new Date(s.from_date) > new Date()).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Pulsante Nuova Sospensione */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShowForm(!showForm)}
                            color={showForm ? "secondary" : "primary"}
                        >
                            {showForm ? 'Nascondi Form' : 'Nuova Sospensione'}
                        </Button>
                        
                        <Button
                            variant="outlined" 
                            onClick={loadSuspensions}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : undefined}
                        >
                            Ricarica
                        </Button>
                    </Box>

                    {/* Form Creazione Sospensione */}
                    {showForm && (
                        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Crea Nuova Sospensione su RECUP
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Tipo Sospensione"
                                            value={form.suspensionType}
                                            onChange={(e) => handleFormChange('suspensionType', e.target.value)}
                                            required
                                        >
                                            {SUSPENSION_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Data Inizio"
                                            value={form.fromDate}
                                            onChange={(e) => handleFormChange('fromDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Data Fine"
                                            value={form.toDate}
                                            onChange={(e) => handleFormChange('toDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Motivo *"
                                            value={form.reason}
                                            onChange={(e) => handleFormChange('reason', e.target.value)}
                                            placeholder="Es: Ferie medico, Manutenzione apparecchiature..."
                                            required
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Descrizione (opzionale)"
                                            value={form.description}
                                            onChange={(e) => handleFormChange('description', e.target.value)}
                                            placeholder="Dettagli aggiuntivi sulla sospensione..."
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={form.notifyPatients}
                                                    onChange={(e) => handleFormChange('notifyPatients', e.target.checked)}
                                                />
                                            }
                                            label="Notifica pazienti con appuntamenti nel periodo"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleCreateSuspension}
                                                disabled={formLoading || !form.reason.trim()}
                                                startIcon={formLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                                            >
                                                {formLoading ? 'Creazione...' : 'Crea su RECUP'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setShowForm(false)}
                                                startIcon={<CancelIcon />}
                                            >
                                                Annulla
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Lista Sospensioni */}
                    <Typography variant="h6" gutterBottom>
                        Sospensioni Attive e Future
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : suspensions.length === 0 ? (
                        <Alert severity="info">
                            Nessuna sospensione trovata per questa agenda.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Periodo</TableCell>
                                        <TableCell>Motivo</TableCell>
                                        <TableCell>Stato</TableCell>
                                        <TableCell>Notifiche</TableCell>
                                        <TableCell>Creata</TableCell>
                                        <TableCell>Azioni</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {suspensions.map((suspension) => {
                                        const typeInfo = getSuspensionTypeInfo(suspension.suspension_type);
                                        const isActive = isActiveSuspension(suspension);
                                        const isFuture = new Date(suspension.from_date) > new Date();
                                        
                                        return (
                                            <TableRow 
                                                key={suspension.id}
                                                sx={{ 
                                                    bgcolor: isActive ? 'warning.light' : isFuture ? 'info.light' : 'inherit',
                                                    opacity: suspension.active ? 1 : 0.6
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip 
                                                        label={typeInfo.label} 
                                                        color={typeInfo.color} 
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            <strong>Da:</strong> {formatDate(suspension.from_date)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>A:</strong> {formatDate(suspension.to_date)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {suspension.reason}
                                                    </Typography>
                                                    {suspension.description && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {suspension.description}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isActive ? (
                                                        <Chip label="ATTIVA ORA" color="warning" size="small" />
                                                    ) : isFuture ? (
                                                        <Chip label="FUTURA" color="info" size="small" />
                                                    ) : (
                                                        <Chip label="SCADUTA" color="default" size="small" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={suspension.notify_patients ? "SÌ" : "NO"} 
                                                        color={suspension.notify_patients ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {formatDate(suspension.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Elimina sospensione">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleDeleteSuspension(suspension)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        Chiudi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar per notifiche */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SuspensionsDialog;